import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import {Box, Button, IconButton, Stack, Typography} from '@mui/material';
import {useCallback, useRef, useState} from 'react';
import {Base64ObjectInput} from '@cp/model';
import ImageDisplayFromStorage from '../../atoms/imageDisplayFromStorage/ImageDisplayFromStorage';
import useImagePaste from './useImagePaste';

export interface ImageUploadProps {
  currentImageUrl?: string | null;
  onImageChange?: (image: Base64ObjectInput | null) => void;
  maxSizeBytes?: number;
  label?: string;
}

export default function BondImageUpload({
  currentImageUrl,
  onImageChange,
  maxSizeBytes = 1 * 1024 * 1024, // 1 MB default
  label = 'Image (Optional)',
}: ImageUploadProps) {
  const [previewImage, setPreviewImage] = useState<Base64ObjectInput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processImageFile = useCallback(
    async (file: File) => {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      if (file.size > maxSizeBytes) {
        setError(`Image size must be less than ${Math.round(maxSizeBytes / 1024 / 1024)} MB`);
        return;
      }

      setError(null);
      try {
        // Use FileReader for better browser compatibility, especially for pasted images
        const base64Object = await new Promise<Base64ObjectInput>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove data URL prefix (e.g., "data:image/png;base64,")
            const base64Data = result.includes(',') ? result.split(',')[1] : result;
            resolve({
              data: base64Data,
              name: file.name || 'image.png',
              type: file.type || 'image/png',
            });
          };
          reader.onerror = () => {
            reject(new Error('Failed to read file'));
          };
          reader.readAsDataURL(file);
        });

        setPreviewImage(base64Object);
        onImageChange?.(base64Object);
      } catch (err) {
        console.error('Error processing image:', err);
        setError(err instanceof Error ? err.message : 'Failed to process image');
      }
    },
    [maxSizeBytes, onImageChange],
  );

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const {files} = e.target;
      if (!files || files.length === 0) return;

      await processImageFile(files[0]);
    },
    [processImageFile],
  );

  const handleRemoveImage = useCallback(() => {
    setPreviewImage(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onImageChange?.(null);
  }, [onImageChange]);

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  useImagePaste({onImagePaste: processImageFile});

  const displayImageUrl = previewImage ? undefined : currentImageUrl;
  const hasImage = !!previewImage || !!currentImageUrl;

  return (
    <Stack spacing={2} data-testid="bond-image-upload">
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
        <Typography variant="subtitle2">{label}</Typography>
        {hasImage && (
          <IconButton size="small" color="error" onClick={handleRemoveImage}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {displayImageUrl && (
        <ImageDisplayFromStorage imageUrl={displayImageUrl} sx={{maxHeight: 300, objectFit: 'contain'}} />
      )}

      {previewImage && (
        <Box
          component="img"
          src={`data:${previewImage.type};base64,${previewImage.data}`}
          alt="Preview"
          sx={{
            maxWidth: '100%',
            maxHeight: 300,
            objectFit: 'contain',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      )}

      <Button
        variant="outlined"
        startIcon={<CloudUploadIcon />}
        onClick={handleButtonClick}
        fullWidth
        sx={{
          borderColor: 'divider',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      >
        {hasImage ? 'Change Image' : 'Upload Image'}
      </Button>

      <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} style={{display: 'none'}} />

      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary">
        Maximum file size: {Math.round(maxSizeBytes / 1024 / 1024)} MB. Supported formats: JPG, PNG, GIF, WebP. You can
        also paste an image from your clipboard.
      </Typography>
    </Stack>
  );
}
